// THE DUPLICATE CHECK's read (spec 09 §3.13, §5's E2-2 row 14, D-324 rulings
// 1 to 3; P3-E2 PR E2-2c).
//
// `POST /v1/customers/check-duplicates` is a dry run: it writes nothing, so it
// is a QUERY here rather than a mutation. React-query then does the three
// things the New customer form needs from a check that follows typing. It
// keys the answer on what was typed, so the panel shows the answer to the
// names on screen and never a late answer to names already replaced. It
// aborts the request a newer input superseded (`signal`). And it keeps the
// last answer on screen while the next one is asked (`keepPreviousData`), so
// the panel does not collapse and re-open on every pause.
//
// It sends no `Idempotency-Key`: route 14 is the one sales POST exempt from it
// (spec 09 §5's E2-2 row 14, D-333 ruling 1), because it writes nothing a
// replay could double and the idempotency store would keep every answer's
// candidate names for good.
//
// THE THROTTLE. The server allows 30 checks a minute per principal (identity's
// `customer_dup_check` window). The debounce in the panel keeps a typist well
// under it, and nothing here asks again on its own: no retry, no refetch on
// focus or reconnect, and `staleTime` 0 only so that the same names typed into
// a NEW form are checked again (a customer created since must be found). A 429
// is one muted line in the panel, the same line as any failed check, because
// the create never depends on the check.

import { keepPreviousData, useQuery } from "@tanstack/react-query";
import type { components } from "@assessly/api-client";
import { client } from "../../../../lib/api";
import { unwrap } from "../../../../lib/request";

export type DuplicateCheckBody = components["schemas"]["CustomerDuplicateCheckRequest"];
export type DuplicateCheckAnswer = components["schemas"]["CustomerDuplicateCheckResponse"];
export type DuplicateCandidate = components["schemas"]["CustomerDuplicateCandidate"];

export const duplicateKeys = {
  /** Its own `sales` branch: a create invalidates the customer lists, and a
   * check is asked again by mounting a new form, not by that invalidation. */
  check: (body: DuplicateCheckBody | undefined) =>
    ["sales", "duplicate-check", body ?? null] as const,
};

/** The check for `body`, or an idle read when there is nothing to check. */
export function useDuplicateCheck(body: DuplicateCheckBody | undefined) {
  return useQuery({
    queryKey: duplicateKeys.check(body),
    enabled: body !== undefined,
    queryFn: async ({ signal }): Promise<DuplicateCheckAnswer> => {
      const res = await client.POST("/v1/customers/check-duplicates", {
        body: body ?? {},
        signal,
      });
      return unwrap(res, "Could not check for similar customers");
    },
    placeholderData: keepPreviousData,
    staleTime: 0,
    gcTime: 30_000,
    retry: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
